/** @graphql agg **/
import { useMemo } from 'react';
import type { GraphQLTaggedNode } from 'react-relay';
import { graphql } from 'react-relay';

import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';

import type {
	useFetchSuggestionCountQuery$data,
	useFetchSuggestionCountQuery$variables,
} from './__generated__/useFetchSuggestionCountQuery.graphql.ts';

export const RemoteObjectSuggestionCountFragment: GraphQLTaggedNode = graphql`
	fragment useFetchSuggestionCount_query on Query {
		confluence_remoteObjectSuggestionCount(cloudId: $cloudId, spaceKey: $spaceKey)
	}
`;

export const RemoteObjectSuggestionCountQuery: GraphQLTaggedNode = graphql`
	query useFetchSuggestionCountQuery($cloudId: ID!, $spaceKey: String!) {
		...useFetchSuggestionCount_query @relay(mask: false)
	}
`;

export type UseFetchSuggestionCountResult = {
	count: number;
	isLoading: boolean;
	isSuppressed: boolean;
	refetch: () => void;
};

type UseFetchSuggestionCountProps = {
	spaceKey?: string;
	cloudId: string;
};

export const useFetchSuggestionCount = ({
	spaceKey,
	cloudId,
}: UseFetchSuggestionCountProps): UseFetchSuggestionCountResult => {
	// useQueryInterop must be called unconditionally; the skip option prevents
	// network activity when there is no space key.
	const {
		data: apolloData,
		loading: apolloLoading,
		refetch: apolloRefetch,
	} = useQueryInterop<useFetchSuggestionCountQuery$data, useFetchSuggestionCountQuery$variables>(
		RemoteObjectSuggestionCountQuery,
		{
			variables: { cloudId, spaceKey: spaceKey ?? '' },
			skip: !spaceKey,
			interopApolloAggClient: true,
		},
	);

	return useMemo<UseFetchSuggestionCountResult>(() => {
		const raw = apolloData?.confluence_remoteObjectSuggestionCount;
		// null data is the suppression signal — this includes the case where the
		// backend returns errors alongside null data (e.g. NotFoundException when
		// the user has been permanently suppressed). Do not gate on apolloError.
		const isSuppressed = !apolloLoading && raw == null;
		return {
			count: raw ?? 0,
			isLoading: apolloLoading,
			isSuppressed,
			refetch: () => {
				void apolloRefetch();
			},
		};
	}, [apolloData, apolloLoading, apolloRefetch]);
};
